import React, { useCallback, useContext, useRef, useState } from "react";
import PropTypes from "prop-types";
import { HStack, Heading, Image, Text, VStack } from "native-base";
import { FiDownload } from "react-icons/fi";
import moment from "moment";
import html2canvas from "html2canvas";
import styles from "./styles.module.css";
import colors from "../../constants/colors";
import Itinerary from "../itinerary";
import { StatusContext } from "../../contexts";
import Cell from "../cell";
import {
  formatCouponValue,
  formatToCurrency,
  getOriginalPrice,
} from "../../utils";
import logo from "../../assets/images/logo.png";

function DownloadQuotationButton(props) {
  const { id, disabled, itinerary, results, hideDiscount } = props;
  const container = useRef();
  const status = useContext(StatusContext);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const onDownloadQuotation = useCallback(async () => {
    try {
      setIsDownloading(true);
      const canvas = await html2canvas(container.current);
      const imgData = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = imgData;
      a.download = `itinerary_${id}_${moment().format("Y_MM_DD_HH_mm_ss")}.png`;
      a.click();
      a.remove();
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsDownloading(false);
  }, [id, status]);

  return (
    <HStack alignItems="flex-start">
      <button
        onClick={onDownloadQuotation}
        disabled={disabled || isDownloading || !isImageLoaded}>
        <HStack
          backgroundColor={colors.third}
          paddingX={5}
          paddingY={2}
          borderRadius="md"
          space={3}>
          <FiDownload color={colors.white} size={20} />
          <Text color={colors.white}>Descargar</Text>
        </HStack>
      </button>
      <VStack
        ref={container}
        padding={5}
        width="100vw"
        space={5}
        position="absolute"
        left="50vw"
        maxWidth="1200">
        <HStack>
          <Image
            src={logo}
            alt="logo"
            height={60}
            width={200}
            onLoadEnd={() => setIsImageLoaded(true)}
          />
          <Heading flex={1} textAlign="center">
            Cotización #{id}
          </Heading>
        </HStack>
        <HStack space={5} alignItems="flex-start">
          <Itinerary detailed itinerary={itinerary} />
          <VStack flex={1} space={5}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <Cell header color={colors.secondary}>
                    Tipo de unidad
                  </Cell>
                  <Cell header color={colors.secondary}>
                    Capacidad
                  </Cell>
                  <Cell header color={colors.secondary}>
                    # de unidades
                  </Cell>
                  <Cell header color={colors.secondary}>
                    Precio unitario
                  </Cell>
                  {!hideDiscount && (
                    <Cell header color={colors.secondary}>
                      Total
                    </Cell>
                  )}
                  {!hideDiscount && (
                    <Cell header color={colors.secondary}>
                      Descuento %
                    </Cell>
                  )}
                  {!hideDiscount && (
                    <Cell header color={colors.secondary}>
                      Descuento $
                    </Cell>
                  )}
                  <Cell header color={colors.secondary}>
                    A pagar
                  </Cell>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => (
                  <tr key={String(index)}>
                    <Cell>{result.vehicleType.name}</Cell>
                    <Cell>{result.vehicleType.capacity}</Cell>
                    <Cell>{result.quantity}</Cell>
                    <Cell>
                      {formatToCurrency(
                        getOriginalPrice(result.price, result.coupon),
                      )}
                    </Cell>
                    {!hideDiscount && (
                      <Cell>
                        {formatToCurrency(
                          getOriginalPrice(result.price, result.coupon) *
                            result.quantity,
                        )}
                      </Cell>
                    )}
                    {!hideDiscount && (
                      <Cell>{formatCouponValue(result.coupon)}</Cell>
                    )}
                    {!hideDiscount && (
                      <Cell>
                        {formatToCurrency(
                          (getOriginalPrice(result.price, result.coupon) -
                            result.price) *
                            result.quantity,
                        )}
                      </Cell>
                    )}
                    <Cell>
                      {formatToCurrency(result.price * result.quantity)}
                    </Cell>
                  </tr>
                ))}
                <tr style={{ backgroundColor: colors.mail }}>
                  <Cell color={colors.white}>Total</Cell>
                  <Cell color={colors.white}>
                    {results
                      .reduce(
                        (total, result) =>
                          total + result.vehicleType.capacity * result.quantity,
                        0,
                      )
                      .toString()}
                  </Cell>
                  <Cell color={colors.white}>
                    {results
                      .reduce((total, result) => total + result.quantity, 0)
                      .toString()}
                  </Cell>
                  <Cell />
                  {!hideDiscount && (
                    <Cell color={colors.white}>
                      {formatToCurrency(
                        results.reduce(
                          (total, result) =>
                            total +
                            getOriginalPrice(result.price, result.coupon) *
                              result.quantity,
                          0,
                        ),
                      )}
                    </Cell>
                  )}
                  {!hideDiscount && (
                    <Cell color={colors.white}>
                      {Array.from(
                        new Set(
                          results.map((result) =>
                            formatCouponValue(result.coupon),
                          ),
                        ),
                      ).join(" ")}
                    </Cell>
                  )}
                  {!hideDiscount && (
                    <Cell color={colors.white}>
                      {formatToCurrency(
                        results.reduce(
                          (total, result) =>
                            total +
                            (getOriginalPrice(result.price, result.coupon) -
                              result.price) *
                              result.quantity,
                          0,
                        ),
                      )}
                    </Cell>
                  )}
                  <Cell color={colors.white}>
                    {formatToCurrency(
                      results.reduce(
                        (total, result) =>
                          total + result.price * result.quantity,
                        0,
                      ),
                    )}
                  </Cell>
                </tr>
              </tbody>
            </table>
            <Text textAlign="right">
              Esta es una cotización únicamente informativa. Vigencia:{" "}
              {moment().add(3, "days").format(moment.HTML5_FMT.DATE)}
            </Text>
          </VStack>
        </HStack>
      </VStack>
    </HStack>
  );
}

DownloadQuotationButton.propTypes = {
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  itinerary: PropTypes.array.isRequired,
  results: PropTypes.array.isRequired,
  hideDiscount: PropTypes.bool,
};

DownloadQuotationButton.defaultProps = {
  hideDiscount: false,
};

export default React.memo(DownloadQuotationButton);
